import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaUser,
  FaSearchLocation,
  FaHandshake,
  FaClipboardCheck,
  FaBriefcase,
  FaShieldAlt,
  FaCheck,
  FaArrowRight,
  FaArrowLeft,
  FaCamera,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../../components/Header';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

const sections = [
  { id: 1, title: 'General Questions', icon: FaUser },
  { id: 2, title: 'Scouting Approach', icon: FaSearchLocation },
  { id: 3, title: 'Network & Relationships', icon: FaHandshake },
  { id: 4, title: 'Evaluation & Decision-Making', icon: FaClipboardCheck },
  { id: 5, title: 'Logistics & Administration', icon: FaBriefcase },
  { id: 6, title: 'KSA Specifics', icon: FaCheck },
  { id: 7, title: 'Security', icon: FaShieldAlt },
];

const initialFormData = {
  // General Questions
  general_experience: '',
  general_identify_talent: '',
  general_age_groups: '',
  // Scouting Approach
  scouting_trends: '',
  scouting_evaluation: '',
  scouting_potential: '',
  // Network & Relationships
  network_build: '',
  network_existing: '',
  network_collaborate: '',
  // Evaluation & Decision-Making
  eval_criteria: '',
  eval_example: '',
  eval_instinct_data: '',
  // Logistics & Administration
  logistics_schedule: '',
  logistics_tools: '',
  logistics_compliance: '',
  // KSA Specifics
  ksa_knowledge: '',
  ksa_contribute: '',
  ksa_improve: '',
  // Security
  security_hail_from_location: '',
  security_present_living: '',
  security_residence_status: '',
  security_residence_other: '',
  security_gps_address: '',
  security_locality: '',
  security_referee_name: '',
  security_referee_relationship: '',
  security_referee_phone: '',
  security_passport_photo: null,
};

export default function ScoutQuestionnaire() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo must be less than 5MB');
        return;
      }
      setFormData((prev) => ({ ...prev, security_passport_photo: file }));
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const validateStep = () => {
    const required = {
      1: ['general_experience', 'general_identify_talent', 'general_age_groups'],
      2: ['scouting_trends', 'scouting_evaluation', 'scouting_potential'],
      3: ['network_build', 'network_existing', 'network_collaborate'],
      4: ['eval_criteria', 'eval_example', 'eval_instinct_data'],
      5: ['logistics_schedule', 'logistics_tools', 'logistics_compliance'],
      6: ['ksa_knowledge', 'ksa_contribute', 'ksa_improve'],
      7: [
        'security_hail_from_location',
        'security_present_living',
        'security_residence_status',
        'security_gps_address',
        'security_locality',
        'security_referee_name',
        'security_referee_relationship',
        'security_referee_phone',
      ],
    };

    const fields = required[step] || [];
    for (const field of fields) {
      if (!formData[field] || formData[field].toString().trim() === '') {
        toast.error('Please answer all questions before continuing.');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => Math.min(s + 1, 7));
  };

  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const handleSubmit = async () => {
    if (!validateStep()) return;

    const scoutId = id;
    if (!scoutId) {
      toast.error('Missing scout ID in URL. Use /auth/scout-questionnaire/:id');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = new FormData();

      data.append('scout_id', String(scoutId));
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null) data.append(key, value);
      });
      data.append('application_status', 'pending');
      data.append('decision_notes', '');
      data.append('decided_by', '');
      data.append('decided_at', '');

      const response = await axios.post(
        ' https://ksa-manager-back.onrender.com/scout_questionnaires',
        data,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const successMessage =
        response.data?.id && response.data?.message
          ? `${response.data.message} (#${response.data.id})`
          : response.data?.message || 'Scout questionnaire submitted successfully';

      toast.success(successMessage);
      setTimeout(() => navigate('/auth/success'), 1500);
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Submission failed. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
  };

  const renderTextarea = (name, label, placeholder) => (
    <div className="mb-5">
      <label className="mb-2 block text-sm font-semibold text-gray-800 sm:text-[15px]">
        {label}
      </label>
      <textarea
        name={name}
        value={formData[name]}
        onChange={handleChange}
        placeholder={placeholder}
        rows={4}
        className="w-full resize-none rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 sm:text-sm"
      />
    </div>
  );

  const renderInput = (name, label, placeholder, type = 'text') => (
    <div className="mb-5">
      <label className="mb-2 block text-sm font-semibold text-gray-800 sm:text-[15px]">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        placeholder={placeholder}
        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-400 transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 sm:text-sm"
      />
    </div>
  );

  const renderRadio = (name, label, options) => (
    <div className="mb-5">
      <label className="mb-3 block text-sm font-semibold text-gray-800 sm:text-[15px]">
        {label}
      </label>
      <div className="flex flex-wrap gap-2.5 sm:gap-3">
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`flex flex-1 cursor-pointer items-center gap-2 rounded-xl border px-4 py-3 text-sm transition-all sm:flex-none sm:py-2.5 ${
              formData[name] === opt.value
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={formData[name] === opt.value}
              onChange={handleChange}
              className="hidden"
            />
            <div
              className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                formData[name] === opt.value
                  ? 'border-blue-500'
                  : 'border-gray-400'
              }`}
            >
              {formData[name] === opt.value && (
                <div className="h-2 w-2 rounded-full bg-blue-500" />
              )}
            </div>
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );

  const renderSection = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <h3 className="mb-6 text-xl font-bold text-gray-900">
              General Questions
            </h3>
            {renderTextarea(
              'general_experience',
              '1. What experience do you have in football scouting or coaching?',
              'e.g. I have 5 years of experience in football scouting, including roles at…'
            )}
            {renderTextarea(
              'general_identify_talent',
              '2. How do you identify talented young players?',
              'e.g. I identify talented players through matches, trials, focusing on technical skills…'
            )}
            {renderTextarea(
              'general_age_groups',
              '3. What age groups have you worked with, and what were your responsibilities?',
              "e.g. I've worked with U10-U20 age groups, responsible for scouting and player development…"
            )}
          </div>
        );

      case 2:
        return (
          <div>
            <h3 className="mb-6 text-xl font-bold text-gray-900">
              Scouting Approach
            </h3>
            {renderTextarea(
              'scouting_trends',
              '1. How do you stay updated on local and international football trends?',
              'e.g. I follow football news, attend matches, and network with coaches/scouts…'
            )}
            {renderTextarea(
              'scouting_evaluation',
              "2. Describe your process for evaluating a player's technical, tactical, physical, and psychological attributes.",
              'e.g. I assess technical skills through drills, tactical awareness through game situations…'
            )}
            {renderTextarea(
              'scouting_potential',
              "3. How do you assess a player's potential for development?",
              'e.g. I look at their work ethic, adaptability, and progress over time…'
            )}
          </div>
        );

      case 3:
        return (
          <div>
            <h3 className="mb-6 text-xl font-bold text-gray-900">
              Network & Relationships
            </h3>
            {renderTextarea(
              'network_build',
              '1. How do you build relationships with coaches, agents, and other scouts?',
              'e.g. I network at matches, tournaments, and conferences…'
            )}
            {renderTextarea(
              'network_existing',
              '2. Do you have an existing network of contacts in Ghana or globally?',
              'e.g. Yes, I have contacts in the Ghana Premier League, regional scouts…'
            )}
            {renderTextarea(
              'network_collaborate',
              '3. How do you collaborate with academy coaches and management?',
              'e.g. I share reports, discuss player potential, and align on academy goals…'
            )}
          </div>
        );

      case 4:
        return (
          <div>
            <h3 className="mb-6 text-xl font-bold text-gray-900">
              Evaluation & Decision-Making
            </h3>
            {renderTextarea(
              'eval_criteria',
              '1. What criteria do you use to recommend a player for the academy?',
              "e.g. I consider technical ability, attitude, physical potential, and fit with the academy's style…"
            )}
            {renderTextarea(
              'eval_example',
              '2. Can you give an example of a player you identified and how you handled the process?',
              'e.g. I identified a player through a local tournament, recommended them, and they progressed…'
            )}
            {renderTextarea(
              'eval_instinct_data',
              '3. How do you balance instinct with data-driven decision-making?',
              'e.g. I use data to support my instincts, but also trust my experience…'
            )}
          </div>
        );

      case 5:
        return (
          <div>
            <h3 className="mb-6 text-xl font-bold text-gray-900">
              Logistics & Administration
            </h3>
            {renderTextarea(
              'logistics_schedule',
              '1. How do you manage your scouting schedule and travel?',
              'e.g. I use a calendar app to organize schedules and plan travel efficiently…'
            )}
            {renderTextarea(
              'logistics_tools',
              '2. What tools or software do you use for player tracking and reporting?',
              'e.g. I use Wyscout, SportsCode, and custom spreadsheets…'
            )}
            {renderTextarea(
              'logistics_compliance',
              '3. How do you ensure compliance with regulations (e.g., FIFA, GFA)?',
              "e.g. I stay updated on regulations and work with the academy's compliance team…"
            )}
          </div>
        );

      case 6:
        return (
          <div>
            <h3 className="mb-6 text-xl font-bold text-gray-900">
              Kempshot Sports Academy Specifics
            </h3>
            {renderTextarea(
              'ksa_knowledge',
              '1. What do you know about our academy and its values?',
              "e.g. I've researched Kempshot Sports Academy's focus on development, community, success…"
            )}
            {renderTextarea(
              'ksa_contribute',
              "2. How would you contribute to our academy's growth and success?",
              "e.g. I'd bring experience, network, and scouting skills to identify and develop top talent…"
            )}
            {renderTextarea(
              'ksa_improve',
              '3. Do you have any ideas for improving our scouting network?',
              "e.g. I'd suggest expanding local partnerships and leveraging data analytics…"
            )}
          </div>
        );

      case 7:
        return (
          <div>
            <h3 className="mb-6 text-xl font-bold text-gray-900">
              Security Verification
            </h3>

            {renderRadio(
              'security_hail_from_location',
              '1. Do you hail from your present location?',
              [
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]
            )}

            {renderInput(
              'security_present_living',
              '2. What are you doing presently for a living?',
              'e.g. Football coach, Teacher, Self-employed…'
            )}

            {renderRadio(
              'security_residence_status',
              '3. Residence Status',
              [
                { value: 'own_house', label: 'Own House' },
                { value: 'rented_house', label: 'Rented House' },
                { value: 'perching', label: 'Perching' },
                { value: 'other', label: 'Other' },
              ]
            )}

            {formData.security_residence_status === 'other' &&
              renderInput(
                'security_residence_other',
                'Please specify',
                'Describe your residence status…'
              )}

            {renderInput(
              'security_gps_address',
              '4. GPS Address',
              'e.g. GA-123-4567'
            )}

            {renderInput(
              'security_locality',
              '5. Locality Name',
              'e.g. East Legon, Kumasi Ahodwo…'
            )}

            {renderInput(
              'security_referee_name',
              '6. Referee (Name)',
              'Full name of your referee'
            )}

            {renderRadio(
              'security_referee_relationship',
              'Referee Relationship',
              [
                { value: 'neighbor', label: 'Neighbor' },
                { value: 'house_owner', label: 'House Owner' },
                { value: 'landlord', label: 'Landlord' },
                { value: 'room_owner', label: 'Room Owner' },
              ]
            )}

            {renderInput(
              'security_referee_phone',
              '7. Referee Phone No.',
              'e.g. 024 XXX XXXX',
              'tel'
            )}

            {/* Passport Photo Upload */}
            <div className="mb-5">
              <label className="block text-sm font-semibold text-gray-800 mb-3">
                8. Upload Passport Photo
              </label>
              <div className="flex flex-col items-center gap-4 sm:flex-row">
                <label className="flex h-32 w-32 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 transition-all hover:border-blue-500 hover:bg-blue-50 overflow-hidden">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <>
                      <FaCamera className="mb-2 text-2xl text-gray-400" />
                      <span className="text-xs text-gray-500">Upload Photo</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </label>
                <div className="text-xs text-gray-500">
                  <p>Accepted formats: JPG, PNG</p>
                  <p>Max size: 5MB</p>
                  {photoPreview && (
                    <p className="mt-1 text-green-600 font-medium">Photo uploaded</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const currentSection = sections.find((s) => s.id === step) || sections[0];
  const CurrentIcon = currentSection.icon;

  const renderNav = () => (
    <>
      <button
        onClick={prevStep}
        disabled={step === 1}
        className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-all sm:px-5 ${
          step === 1
            ? 'cursor-not-allowed bg-gray-100 text-gray-400'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-[0.98]'
        }`}
      >
        <FaArrowLeft className="text-xs" />
        Previous
      </button>

      {step < 7 ? (
        <button
          onClick={nextStep}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition-all hover:from-blue-600 hover:to-blue-700 active:scale-[0.98] sm:flex-none sm:px-6"
        >
          Next
          <FaArrowRight className="text-xs" />
        </button>
      ) : (
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-green-500/25 transition-all hover:from-green-600 hover:to-emerald-700 active:scale-[0.98] disabled:opacity-50 sm:flex-none sm:px-6"
        >
          {isSubmitting ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
          ) : (
            <FaCheck className="text-xs" />
          )}
          {isSubmitting ? 'Submitting…' : 'Submit'}
        </button>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme="light"
      />
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-800 px-4 pb-14 pt-20 sm:px-6 sm:pb-20 sm:pt-24 lg:px-8">
        <div className="relative mx-auto max-w-3xl text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl font-extrabold text-white sm:text-4xl md:text-5xl"
          >
            Scout Interview{' '}
            <span className="text-blue-200">Questionnaire</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mx-auto mt-2 max-w-xl text-sm text-blue-100 sm:mt-4 sm:text-base"
          >
            Complete all sections to finalize your application with Kempshot
            Sports Academy.
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="relative mx-auto -mt-8 max-w-3xl px-3 pb-28 sm:-mt-12 sm:px-6 sm:pb-12 lg:px-8">
        {/* Stepper header */}
        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
                <CurrentIcon />
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  Step {step} of {sections.length}
                </p>
                <p className="truncate text-sm font-bold text-gray-900 sm:text-base">
                  {currentSection.title}
                </p>
              </div>
            </div>
            <span className="shrink-0 text-sm font-bold text-blue-600">
              {Math.round((step / sections.length) * 100)}%
            </span>
          </div>

          {/* Segmented progress */}
          <div className="flex items-center gap-1.5">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  if (step > s.id) setStep(s.id);
                }}
                aria-label={s.title}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  step >= s.id ? 'bg-blue-500' : 'bg-gray-200'
                } ${step > s.id ? 'cursor-pointer' : 'cursor-default'}`}
              />
            ))}
          </div>
        </div>

        {/* Questions */}
        <div className="mt-5 sm:mt-6">
          <AnimatePresence mode="wait" custom={1}>
            <motion.div
              key={step}
              custom={1}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {renderSection()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Tablet / desktop navigation */}
        <div className="mt-6 hidden items-center justify-between gap-4 sm:flex">
          {renderNav()}
        </div>
      </div>

      {/* Mobile sticky navigation */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur-md sm:hidden">
        <div className="flex items-center gap-3">
          {renderNav()}
        </div>
      </div>
    </div>
  );
}
